import { useMemo } from 'react';
import { Message, MessageCitation } from '@/types/chat';
import { extractUrls } from '@/lib/urlUtils';
import { normalizeUrl } from '@/lib/urlNormalize';
import { useNormalizedMessageCards } from '@/hooks/useNormalizedMessageCards';

export type CitationSource = 'api' | 'extracted';

export interface MessageCitationsResult {
  citations: MessageCitation[];
  source: CitationSource;
  hasCitations: boolean;
}

/**
 * Extracts citations from a message, prioritizing API-provided citations
 * and falling back to content extraction if needed.
 * 
 * @param message - The message to extract citations from
 * @returns Citations with source tracking
 */
export function useMessageCitations(message: Message | null): MessageCitationsResult {
  const { cards: normalizedCards } = useNormalizedMessageCards(message, { enabled: true });

  return useMemo(() => {
    if (!message) {
      return { citations: [], source: 'api', hasCitations: false };
    }

    // Priority 1: Use API-provided citations if available
    if (message.citations && message.citations.length > 0) {
      return {
        citations: message.citations,
        source: 'api',
        hasCitations: true,
      };
    }

    // Priority 2: Extract citations from message content (including idea cards)
    const allContent = [
      message.content || '',
      ...normalizedCards.map(card => card.displayBody || card.body || '').filter(Boolean),
    ].join('\n');

    const urls = extractUrls(allContent);
    if (urls.length === 0) {
      return { citations: [], source: 'extracted', hasCitations: false };
    }

    // Convert URLs to citations
    const extractedCitations = urls
      .map((url, idx) => {
        try {
          const normalized = normalizeUrl(url);
          return {
            id: `extracted-${message.id}-${idx}`,
            url: normalized,
            title: new URL(url).hostname.replace(/^www\./i, ''),
            index: idx + 1,
            snippet: undefined as string | undefined,
          } as MessageCitation;
        } catch {
          return null;
        }
      })
      .filter((c): c is MessageCitation => c !== null);

    // Remove duplicates by normalized URL
    const seenUrls = new Set<string>();
    const uniqueCitations = extractedCitations.filter(citation => {
      const normalized = normalizeUrl(citation.url);
      if (seenUrls.has(normalized)) return false;
      seenUrls.add(normalized);
      return true;
    });

    return {
      citations: uniqueCitations,
      source: 'extracted',
      hasCitations: uniqueCitations.length > 0,
    };
  }, [message, normalizedCards]);
}
